/**
 * Cue → sample map (DESIGN-client §8 table). All Kenney CC0 unless noted. Sample ids are the
 * keys of `public/assets/audio/sfx.json` produced by tools/audio/pack-sprites.ts; keep every id
 * in `S` so a rename is one edit. `cueSfx` is checked exhaustive against `CueType` at compile
 * time (and by sfxMap.test.ts at run time).
 *
 * Sounds tied to a scene animation rather than a cue (per-card deal slides, bid thocks, hover,
 * tally ticks, fuse) live in `uiSfx` and are triggered by the objects that animate them.
 */
import type { CueData, CueType } from '../scene/cues/cueTypes';
import type { Bus } from './AudioEngine';

export interface SfxPlay {
  /** One id, or variants to pick from at random. */
  id: string | readonly string[];
  /** ± random pitch spread (0.04 = ±4%). */
  pitchVar?: number;
  /** Base pitch multiplier (default 1). */
  pitch?: number;
  gain?: number;
  bus?: Bus;
  delayMs?: number;
}

export type SfxResolver<T extends CueType> = (data: CueData[T]) => readonly SfxPlay[];
export type SfxEntry<T extends CueType> = readonly SfxPlay[] | SfxResolver<T> | null;
export type CueSfxMap = { readonly [T in CueType]: SfxEntry<T> };

/**
 * Sample ids inside the sfx sprite.
 *
 * These are the keys of `public/assets/audio/sfx.json`, which `tools/audio/pack-sprites.ts` owns —
 * not the vendored source filenames DESIGN §8 lists. The two used to be confused: every id here
 * was the source filename (`impactWood_heavy_000`, `click_003`, `jingle_short_neg`), the sprite
 * had none of them, and so every `engine.play()` in the game missed its sample and returned
 * silently. `sfxMap.test.ts` now reads the sprite manifest and asserts the two agree, so a repack
 * that renames an id fails the unit suite rather than shipping a silent build.
 */
export const S = {
  cardSlide: ['card_slide_1', 'card_slide_2', 'card_slide_3'],
  cardShove1: 'card_shove_1',
  cardPlace: ['card_place_1', 'card_place_2', 'card_place_3', 'card_place_4'],
  cardPlace3: 'card_place_3',
  chipLay1: 'chip_lay_1',
  chipLay2: 'chip_lay_2',
  chipLay3: 'chip_lay_3',
  cardsPackTakeOut: 'cards_pack_take_out',
  cardsPackOpen: 'cards_pack_open',
  dealRiffle: 'deal_riffle',
  chipFlip: 'chip_flip',
  chipsStack1: 'chips_stack_1',
  chipsCollide1: 'chips_collide_1',
  click: 'click',
  error: 'error',
  tick1: 'tick_1',
  tick2: 'tick_2',
  jingleNeg: 'jingle_neg',
  jingleWin: 'jingle_win',
  impactSoft: 'impact_soft',
  /** Two heavies ship, not three: tier 3 is separated by `table_slam`, not by a third wood hit. */
  impactWoodHeavy: ['impact_wood_heavy_1', 'impact_wood_heavy_2'],
  impactWoodLight: 'impact_wood_light',
  impactPunch: 'impact_punch',
  impactMetalHeavy: 'impact_metal_heavy',
  pencil: 'pencil',
  tableSlam: 'table_slam',
  crowdOoh: 'crowd_ooh',
  pop: 'pop',
  /** Low sub-rumble. Shipped in the sprite from the start; the nil sting is its first caller. */
  rumble: 'rumble',
} as const;

const slamSfx = (tier: 1 | 2 | 3): SfxPlay[] => {
  const out: SfxPlay[] = [
    { id: S.cardPlace3, pitchVar: 0.03 },
    { id: S.impactWoodHeavy[Math.min(tier, 2) - 1] ?? S.impactWoodHeavy[0], pitchVar: 0.03 },
  ];
  if (tier === 3) out.push({ id: S.tableSlam, pitchVar: 0.03, delayMs: 30 });
  return out;
};

export const cueSfx = {
  // The riffle that opens the deal; the per-card slides under it are played by the deal
  // animation (uiSfx.dealCard) on a 40ms stagger.
  dealt: [{ id: S.dealRiffle, pitchVar: 0.03, gain: 0.8 }],
  blindNilOffered: null,
  sealBroken: [{ id: S.impactSoft, pitchVar: 0.04 }],
  bidLocked: (d) => (d.auto ? [{ id: S.chipFlip, pitchVar: 0.03, gain: 0.6 }] : uiSfx.chipLock),
  /**
   * The four chips turn over. A nil among them earns the declaration sting on top (§4.2 "nil
   * declaration sting"): the commit itself is silent to everyone but the bidder, because a bid is
   * secret until this cue, so the reveal is the first moment the table is allowed to hear it.
   */
  bidsRevealed: (d) => {
    const out: SfxPlay[] = [{ id: S.chipsStack1 }];
    // `Array.isArray` rather than a bare read: the sfx suite fires every cue type with an empty
    // payload to prove the map never throws on one.
    if (Array.isArray(d.bids) && d.bids.some((b) => b.kind !== 'tricks')) {
      out.push({ id: S.impactMetalHeavy, pitch: 0.72, gain: 0.85, delayMs: 260 });
      out.push({ id: S.crowdOoh, gain: 0.5, delayMs: 430 });
    }
    return out;
  },
  cardLocked: (d) => (d.slam ? [{ id: S.impactWoodHeavy[0], pitchVar: 0.03 }] : [{ id: S.cardShove1, pitch: 0.92, pitchVar: 0.04, gain: 0.7 }]),
  cardUnlocked: (d) => (d.reason === 'illegal' ? uiSfx.lockBounce : [{ id: S.cardShove1, pitchVar: 0.04, gain: 0.5 }]),
  cardPlayed: (d) => (d.slam === 0 ? [{ id: S.cardPlace, pitchVar: 0.04 }] : slamSfx(d.slam)),
  trickWon: (d) => {
    const out: SfxPlay[] = [{ id: S.cardsPackOpen, pitchVar: 0.04, gain: d.cascade ? 0.7 : 1 }];
    if (d.isBag) out.push({ id: S.chipLay3, pitch: 0.94, pitchVar: 0.04, delayMs: 600 });
    return out;
  },
  bostonWatch: null,
  /**
   * The server has just spent one of my banked seconds for me.
   *
   * This was a plain `click` — the same sample the bid chips and half the HUD use — so the one cue
   * that says "you are running out of time and the table is paying for it" was the quietest,
   * least distinct sound in the game and playtesters never noticed it. A struck bell, then a slow
   * clock under it: nothing else in the map ticks twice.
   */
  timeBankUsed: [
    { id: S.impactMetalHeavy, pitch: 1.5, gain: 0.5 },
    { id: S.tick2, pitch: 0.8, gain: 0.85, delayMs: 200 },
    { id: S.tick2, pitch: 0.8, gain: 0.85, delayMs: 560 },
  ],
  timeout: null,
  botTakeover: null,
  humanReturned: null,
  seatDisconnected: null,
  seatReconnected: null,
  seatChanged: null,
  // Count-up ticks are driven by Counter (uiSfx.tallyTick, rate 0.9→1.25).
  handScored: null,
  set: [{ id: S.impactMetalHeavy, pitch: 0.8 }],
  bagPenalty: [{ id: S.chipsCollide1 }, { id: S.impactWoodLight, delayMs: 40 }],
  boston: [{ id: S.jingleWin }, { id: S.crowdOoh, delayMs: 200 }],
  // Owned by `NilSetPiece.result`, which plays `uiSfx.nilMade` / `uiSfx.nilBroken` on the beat it
  // animates. Playing them from here as well doubled every gold flip and every crack.
  nilResult: null,
  suddenDeath: null,
  comeback: null,
  matchPoint: null,
  matchEnd: [{ id: S.jingleWin }],
  // Handled by Announcer (announcer bus).
  announcerBark: null,
  emote: [{ id: S.pop, pitchVar: 0.04 }],
  // Fuse ticks are driven by the HUD countdown (uiSfx.timerFuse).
  timer: null,
  serverRestarting: null,
} satisfies CueSfxMap;

/** Animation-driven sounds (not cue-driven). */
export const uiSfx = {
  dealCard: [{ id: S.cardSlide, pitchVar: 0.04 }],
  hoverLift: [{ id: S.cardShove1, pitchVar: 0.04, gain: 0.35 }],
  /**
   * The turn has come round to me (`TableScene.signalTurn`, once per turn).
   *
   * Deliberately small: it fires up to thirteen times a hand and sits under the fan springing back
   * to full size, which is the loud half of the signal. A chip turning over and a soft pop, both
   * pitched up so it reads as "go" rather than as another card sound.
   */
  yourTurn: [
    { id: S.chipFlip, pitch: 1.2, gain: 0.45 },
    { id: S.pop, pitch: 1.3, gain: 0.4, delayMs: 70 },
  ],
  /** Bid thock; caller passes `pitch: 0.96 + v*0.008`. */
  bidThockUp: [{ id: S.chipLay1 }],
  bidThockDown: [{ id: S.chipLay2 }],
  bidSweepToZero: [{ id: S.cardsPackTakeOut, pitchVar: 0.04 }],
  chipLock: [
    { id: S.chipFlip, pitchVar: 0.03 },
    { id: S.click, pitchVar: 0.03 },
  ],
  /**
   * The nil declaration (§4 "commit requires slam; SFX nil_sting"). It is a dare, not a loss: a
   * metal hit with a sub under it and the room reacting a beat later. It used to be the negative
   * jingle, which told the player they had done something wrong at the exact moment they were
   * being brave.
   */
  nilSting: [
    { id: S.impactMetalHeavy, pitch: 0.72, pitchVar: 0.02 },
    { id: S.rumble, gain: 0.55, delayMs: 20 },
    { id: S.crowdOoh, gain: 0.5, delayMs: 180 },
  ],
  /** The onyx coin landing on the felt: heavy, wooden, with a coin's own settle under it. */
  nilCoinLand: [
    { id: S.impactWoodHeavy[0], pitch: 0.7, pitchVar: 0.03, gain: 0.9 },
    { id: S.chipLay3, pitch: 0.72, pitchVar: 0.04, gain: 0.5, delayMs: 35 },
  ],
  /** The reveal names a nil seat and the coin sets off for the shelf. */
  nilReveal: [{ id: S.chipsStack1, pitch: 0.9 }],
  /** The coin arriving at the end of the band. */
  nilCoinArrive: [
    { id: S.pop, pitchVar: 0.04 },
    { id: S.chipLay1, pitch: 0.85, gain: 0.55 },
  ],
  /** Nil made: gold. */
  nilMade: [
    { id: S.jingleWin, pitch: 1.06 },
    { id: S.chipsStack1, gain: 0.7, delayMs: 90 },
    { id: S.pop, gain: 0.8, delayMs: 210 },
  ],
  /** Nil broken: the coin cracks. */
  nilBroken: [
    { id: S.impactMetalHeavy, pitch: 0.6, pitchVar: 0.02 },
    { id: S.chipsCollide1, gain: 0.8, delayMs: 45 },
    { id: S.jingleNeg, gain: 0.75, delayMs: 150 },
  ],
  lockBounce: [{ id: S.error }],
  /**
   * A card dropped on the lock zone while the lock is shut (§5, `HandLayer.lockOffered`).
   *
   * The same buzz as `lockBounce`, quieter and lower. It fires on a near-miss the player did not
   * mean to make — being a beat late to their own turn — so it has to read as "not that", not as
   * "you did something wrong", and it must not earn a toast.
   */
  lockRefused: [{ id: S.error, pitch: 0.8, gain: 0.3 }],
  ledgerStamp: [
    { id: S.impactPunch, pitchVar: 0.04 },
    { id: S.pencil, pitchVar: 0.04, delayMs: 60 },
  ],
  /**
   * MY team took that book (§3.7). A small good thing, said quietly.
   *
   * It plays ON TOP of `ledgerStamp`, which every book already gets, so it has to sit above that
   * without competing with it: a bright chip shimmer and a pop, both pitched well up and both at
   * about a third gain, ~250ms all in. Deliberately not `jingleWin` — that is the nil payoff and
   * hearing it six times a hand would spend it. Every sample here is from the shipped sprite; the
   * pitch spread keeps thirteen of them in a hand from reading as one looped sound.
   */
  ourBook: [
    { id: S.chipsStack1, pitch: 1.5, pitchVar: 0.04, gain: 0.4 },
    { id: S.pop, pitch: 1.55, pitchVar: 0.05, gain: 0.28, delayMs: 90 },
  ],
  /** Caller passes `pitch: 0.9 + 0.35*progress`. */
  tallyTick: [{ id: S.tick1 }],
  bagDrop: [{ id: S.chipLay3, pitch: 0.94, pitchVar: 0.04 }],
  bagTopple: [{ id: S.chipsCollide1 }, { id: S.impactWoodLight, delayMs: 40 }],
  /** Caller passes `pitch: 1 + 0.02*secondsElapsed`. */
  timerFuse: [{ id: S.tick2 }],
  emote: [{ id: S.pop, pitchVar: 0.04 }],
} as const satisfies Record<string, readonly SfxPlay[]>;

export type UiSfxKey = keyof typeof uiSfx;

export interface ResolvedPlay {
  id: string;
  pitch: number;
  gain: number;
  bus: Bus;
  delayMs: number;
}

/** Concrete plays for `spec`: variant picked, pitch spread applied. */
export function resolvePlays(specs: readonly SfxPlay[], random: () => number = Math.random): ResolvedPlay[] {
  return specs.map((s) => {
    const id = typeof s.id === 'string' ? s.id : (s.id[Math.floor(random() * s.id.length)] ?? s.id[0] ?? '');
    const spread = s.pitchVar ?? 0;
    const pitch = (s.pitch ?? 1) * (1 + (random() * 2 - 1) * spread);
    return { id, pitch, gain: s.gain ?? 1, bus: s.bus ?? 'sfx', delayMs: s.delayMs ?? 0 };
  });
}

/** Plays for a cue (empty when the cue is silent). */
export function resolveCueSfx<T extends CueType>(type: T, data: CueData[T], random: () => number = Math.random): ResolvedPlay[] {
  const entry = cueSfx[type] as SfxEntry<T>;
  if (!entry) return [];
  const specs = typeof entry === 'function' ? entry(data) : entry;
  return resolvePlays(specs, random);
}

/** Every sample id referenced by either map (for asset checks). */
export function allSampleIds(): string[] {
  const ids = new Set<string>();
  for (const v of Object.values(S)) {
    if (typeof v === 'string') ids.add(v);
    else for (const id of v) ids.add(id);
  }
  return [...ids].sort();
}
